import {
  collection, doc, updateDoc, deleteDoc, query, where, orderBy,
  onSnapshot, writeBatch, getDocs, limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import { toDate } from '../models';

export function parseNotification(id, d) {
  return {
    id,
    userId: d.userId || '',
    type: d.type || 'order_update',
    title: d.title || '',
    message: d.message || d.body || '',
    orderId: d.orderId || null,
    restaurantId: d.restaurantId || null,
    driverId: d.driverId || null,
    data: d.data || null,
    isRead: !!d.isRead,
    createdAt: toDate(d.createdAt),
    raw: d,
  };
}

// iOS fetches driver notifications by driverId == uid; docs carry userId too.
// Listen on both fields and merge so nothing is missed either direction.
export function listenToNotifications(userId, cb, onErr) {
  const buckets = { userId: new Map(), driverId: new Map() };
  const emit = () => {
    const merged = new Map([...buckets.userId, ...buckets.driverId]);
    const list = [...merged.values()];
    list.sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
    cb(list.slice(0, 100));
  };
  const errHandler = (e) => { console.error('listenToNotifications', e); onErr && onErr(e); };
  const unsubs = [
    onSnapshot(
      query(collection(db, 'notifications'), where('userId', '==', userId), limit(100)),
      snap => {
        buckets.userId = new Map(snap.docs.map(d => [d.id, parseNotification(d.id, d.data())]));
        emit();
      }, errHandler),
    onSnapshot(
      query(collection(db, 'notifications'), where('driverId', '==', userId), limit(100)),
      snap => {
        buckets.driverId = new Map(snap.docs.map(d => [d.id, parseNotification(d.id, d.data())]));
        emit();
      }, errHandler),
  ];
  return () => unsubs.forEach(u => u());
}

export async function markNotificationRead(id) {
  await updateDoc(doc(db, 'notifications', id), { isRead: true });
}

// Single-equality queries + client-side isRead filter — no composite index.
// Covers both listener buckets (userId + driverId docs).
export async function markAllNotificationsRead(userId) {
  const docs = new Map();
  const collect = (snap) => snap.docs.forEach(d => {
    if (!d.data().isRead) docs.set(d.id, d.ref);
  });
  collect(await getDocs(query(
    collection(db, 'notifications'), where('userId', '==', userId), limit(200))));
  collect(await getDocs(query(
    collection(db, 'notifications'), where('driverId', '==', userId), limit(200))));
  const batch = writeBatch(db);
  docs.forEach(ref => batch.update(ref, { isRead: true }));
  await batch.commit();
}

export async function deleteNotification(id) {
  await deleteDoc(doc(db, 'notifications', id));
}
